import { GameType, GAME_INITIALS, gridGameSet, SavedGrid } from '../persistence/types.js';
import { getGameTitle } from '../games/GameRegistry.js';
import { Position } from '../core/GridGeometry.js';
import { gamesAtRisk } from '../core/generationRules.js';
import { isDefaultGrid } from '../persistence/DefaultGrids.js';
import { PersistenceManager } from '../persistence/PersistenceManager.js';
import { renderGridPreview } from '../rendering/GridPreview.js';
import { GameSetPicker } from './GameSetPicker.js';

/** What the modal is being opened for — a copy of an existing grid, or a pasted grid code. */
export interface DuplicateRequest {
    /** Heading shown above the create-another section. */
    title: string;
    /** The set the picker starts on. */
    initialSet: GameType[];
    /** Label for the confirm button, e.g. "Duplicate" or "Add Grid". */
    confirmLabel: string;
    /** The grid's digit positions, in ascending digit-value order (for the risk check). */
    digits: Position[];
    /** The grid dimension, for the risk check. */
    gridSize: number;
    /**
     * The grid code (digit layout) being duplicated/added. All local grids sharing it are
     * listed above the picker as variants, so a player can trace the copies they've made.
     * It's also the key for the cross-grid "keep solved games" carry.
     */
    sourceGridData: string;
    /** Whether the target is a Vortex grid — themes the picker purple, and selects the
     *  Vortex carry rule (Hub-bound solution set vs. per-game best). */
    isVortex: boolean;
    /** Whether the "keep solved games" carry applies (duplicate: yes; pasted code: no). */
    canCarry: boolean;
}

export interface DuplicateResult {
    /** The chosen set, as a canonical scope string. */
    gameSet: string;
    /** Carry the source's solved games over (always false when nothing was solved). */
    copyScores: boolean;
}

export interface GridDuplicatorCallbacks {
    onConfirm: (result: DuplicateResult) => void;
    onClose: () => void;
    /** Jump to an existing variant: select + play it, closing the modal. */
    onSelectGrid: (gridId: string) => void;
}

/**
 * The variant manager for one grid layout. Top: every local grid that shares the source's
 * grid code, rendered as selector-style cards (click to jump to one) — so a player can trace
 * the copies they've made across different game sets. Bottom: the Grid Creator's set picker
 * on its own, to make one more copy (pick its 12 games, and whether solved games carry over).
 */
export class GridDuplicator {
    private containerEl: HTMLElement;
    private persistence: PersistenceManager;
    private callbacks: GridDuplicatorCallbacks;
    private request: DuplicateRequest | null = null;
    private setPicker: GameSetPicker | null = null;
    private copyScores = true;

    constructor(containerEl: HTMLElement, persistence: PersistenceManager, callbacks: GridDuplicatorCallbacks) {
        this.containerEl = containerEl;
        this.persistence = persistence;
        this.callbacks = callbacks;
    }

    show(request: DuplicateRequest): void {
        this.request = request;
        this.copyScores = this.carriable().length > 0;
        this.setPicker = new GameSetPicker(request.initialSet, () => {
            this.renderCarryLine();
            this.renderRiskWarning();
        });
        // Recolour the whole modal (picker chips, carry toggle, confirm) purple for Vortex.
        this.containerEl.classList.toggle('duplicate-vortex', request.isVortex);
        this.render();
        this.containerEl.classList.remove('hidden');
    }

    hide(): void {
        this.request = null;
        this.setPicker = null;
        this.containerEl.classList.remove('duplicate-vortex');
        this.containerEl.classList.add('hidden');
    }

    /** Games a "keep solved games" carry could bring over, across all same-layout grids. */
    private carriable(): GameType[] {
        if (!this.request || !this.request.canCarry) return [];
        return this.persistence.carriableGames(this.request.sourceGridData, this.request.isVortex);
    }

    private render(): void {
        const request = this.request;
        if (!request || !this.setPicker) return;

        this.containerEl.innerHTML = '';

        this.renderVariants(request);

        const heading = document.createElement('div');
        heading.className = 'grid-section-label';
        heading.textContent = request.title;
        this.containerEl.appendChild(heading);

        this.containerEl.appendChild(this.setPicker.element);

        const warning = document.createElement('div');
        warning.className = 'duplicate-warning hidden';
        this.containerEl.appendChild(warning);

        if (this.carriable().length > 0) {
            this.containerEl.appendChild(this.renderScoreToggle());
        }

        const footer = document.createElement('div');
        footer.className = 'grid-creator-footer duplicate-footer';

        const cancelBtn = document.createElement('button');
        cancelBtn.className = 'action-btn action-cancel';
        cancelBtn.textContent = 'Cancel';
        cancelBtn.addEventListener('click', () => {
            this.hide();
            this.callbacks.onClose();
        });

        const confirmBtn = document.createElement('button');
        confirmBtn.className = 'action-btn action-confirm';
        confirmBtn.textContent = request.confirmLabel;
        confirmBtn.addEventListener('click', () => this.confirm());

        footer.append(cancelBtn, confirmBtn);
        this.containerEl.appendChild(footer);

        this.renderCarryLine();
        this.renderRiskWarning();
    }

    /**
     * All local grids sharing the source's grid code, as read-only selector-style cards.
     * Clicking one jumps to it. Omitted entirely when nothing shares the layout (e.g. a
     * pasted code with no local match), so the modal collapses to just the create section.
     */
    private renderVariants(request: DuplicateRequest): void {
        const variants = this.persistence.getGridsSorted().filter(g => g.gridData === request.sourceGridData);
        if (variants.length === 0) return;

        // Surface the canonical origin first: a challenge outranks a default, both outrank
        // ordinary copies. A stable sort keeps getGridsSorted()'s order within each rank.
        const rank = (g: SavedGrid): number =>
            g.challengeId != null ? 0 : isDefaultGrid(g.gridData) ? 1 : 2;
        variants.sort((a, b) => rank(a) - rank(b));

        const heading = document.createElement('div');
        heading.className = 'grid-section-label';
        heading.textContent = 'Grids with this layout';
        this.containerEl.appendChild(heading);

        const list = document.createElement('div');
        list.className = 'duplicate-variants';
        const currentId = this.persistence.getCurrentGridId();
        for (const g of variants) list.appendChild(this.renderVariantCard(g, g.id === currentId));
        this.containerEl.appendChild(list);
    }

    /** A lean, read-only grid card (no expand/actions); clicking it jumps to that grid. */
    private renderVariantCard(savedGrid: SavedGrid, isCurrent: boolean): HTMLElement {
        const isVortex = savedGrid.gridConfig?.isVortex === true;

        const option = document.createElement('div');
        option.className = 'grid-option';
        if (isCurrent) option.classList.add('current');
        if (savedGrid.isImported) option.classList.add('imported');
        if (isVortex) option.classList.add('vortex');

        const preview = document.createElement('canvas');
        preview.className = 'grid-option-preview';
        preview.width = 80;
        preview.height = 80;
        renderGridPreview(preview, savedGrid.gridData, { isImported: !!savedGrid.isImported, isVortex });

        const info = document.createElement('div');
        info.className = 'grid-option-info';

        // A short label so variants of the same layout are still distinguishable.
        const labelText = this.variantLabel(savedGrid, isVortex);
        if (labelText) {
            const labelEl = document.createElement('div');
            labelEl.className = 'grid-option-label';
            if (savedGrid.challengeId != null) labelEl.classList.add('challenge-label');
            else if (savedGrid.isImported) labelEl.classList.add('imported-label');
            else if (isVortex) labelEl.classList.add('vortex-label');
            else if (savedGrid.copyOf) labelEl.classList.add('copy-label');
            labelEl.textContent = labelText;
            info.appendChild(labelEl);
        }

        const totalScore = this.persistence.getTotalScore(savedGrid.id);
        const totalEl = document.createElement('div');
        totalEl.className = 'grid-option-total';
        if (totalScore !== null) {
            totalEl.textContent = `Total: ${totalScore}`;
            if (savedGrid.isImported) totalEl.classList.add('imported');
            else if (isVortex) totalEl.classList.add('vortex');
        } else {
            totalEl.classList.add('incomplete');
            totalEl.textContent = 'Incomplete';
        }
        info.appendChild(totalEl);

        // Per-game scores (the differentiator between variants is the game set); on narrow
        // screens CSS swaps these for the compact "Cleared: …" line, as in the selector.
        const scoresEl = document.createElement('div');
        scoresEl.className = 'grid-option-scores';
        const cleared: string[] = [];
        for (const gameType of gridGameSet(savedGrid)) {
            const initial = GAME_INITIALS[gameType];
            const best = this.persistence.getBestScore(savedGrid.id, gameType);
            const scoreEl = document.createElement('span');
            scoreEl.className = 'grid-option-score';
            if (best) {
                cleared.push(initial);
                scoreEl.classList.add('solved');
                if (savedGrid.isImported) scoreEl.classList.add('imported');
                else if (isVortex) scoreEl.classList.add('vortex');
                scoreEl.textContent = `${initial}:${best.score.totalScore}`;
            } else {
                scoreEl.textContent = `${initial}:-`;
            }
            scoresEl.appendChild(scoreEl);
        }
        info.appendChild(scoresEl);

        const clearedEl = document.createElement('div');
        clearedEl.className = 'grid-option-cleared';
        if (savedGrid.isImported) clearedEl.classList.add('imported');
        else if (isVortex) clearedEl.classList.add('vortex');
        clearedEl.textContent = cleared.length ? `Cleared: ${cleared.join(' ')}` : 'Cleared: —';
        info.appendChild(clearedEl);

        option.append(preview, info);
        option.addEventListener('click', () => {
            this.hide();
            this.callbacks.onSelectGrid(savedGrid.id);
        });

        const wrapper = document.createElement('div');
        wrapper.className = 'grid-option-wrapper duplicate-variant';
        if (savedGrid.isImported) wrapper.classList.add('imported');
        if (isVortex) wrapper.classList.add('vortex');
        wrapper.appendChild(option);
        return wrapper;
    }

    /** Short tag distinguishing a variant card (mirrors the selector's card labels). */
    private variantLabel(savedGrid: SavedGrid, isVortex: boolean): string {
        if (savedGrid.challengeId != null) {
            return savedGrid.challengeTitle ? `Challenge · ${savedGrid.challengeTitle}` : 'Challenge';
        }
        if (isDefaultGrid(savedGrid.gridData)) return 'Default grid';
        if (savedGrid.isImported && isVortex) return 'Imported · Vortex';
        if (savedGrid.isImported) return 'Imported';
        if (isVortex) return 'Vortex';
        if (savedGrid.copyOf) return `Copy · ${savedGrid.copyOf}`;
        return 'Custom grid';
    }

    /**
     * Soft warning when the chosen set includes a game whose placement rule this grid
     * breaks — the grid may be unsolvable for it. Warn-but-allow: the confirm button stays
     * live (the maintainer is the real solvability gate). Hidden when the set is clean.
     */
    private renderRiskWarning(): void {
        const el = this.containerEl.querySelector('.duplicate-warning');
        if (!el || !this.request || !this.setPicker) return;

        const risky = gamesAtRisk(this.request.digits, this.request.gridSize, this.setPicker.getSelection());
        if (risky.length === 0) {
            el.classList.add('hidden');
            el.textContent = '';
            return;
        }
        const names = risky.map(getGameTitle).join(', ');
        el.classList.remove('hidden');
        el.textContent = `⚠ This grid may be unsolvable for ${names} — proceed with caution.`;
    }

    private renderScoreToggle(): HTMLElement {
        const wrap = document.createElement('div');
        wrap.className = 'duplicate-scores';

        const toggle = document.createElement('button');
        toggle.className = 'duplicate-score-toggle';
        toggle.classList.toggle('on', this.copyScores);
        toggle.textContent = this.copyScores ? '☑ Keep solved games' : '☐ Keep solved games';
        toggle.addEventListener('click', () => {
            this.copyScores = !this.copyScores;
            this.render();
        });
        wrap.appendChild(toggle);

        const carry = document.createElement('div');
        carry.className = 'duplicate-carry-line';
        wrap.appendChild(carry);

        return wrap;
    }

    /**
     * Which solutions actually survive the copy for the current set. Drawn from every
     * same-layout grid — so a game solved only on a sibling still carries — and recomputed
     * as the picker changes. For Vortex the carry comes from a single best-matching Hub, so
     * games solved under a different Hub show as "dropping".
     */
    private renderCarryLine(): void {
        const carry = this.containerEl.querySelector('.duplicate-carry-line');
        if (!carry || !this.request || !this.setPicker) return;

        if (!this.copyScores) {
            carry.textContent = 'Starting fresh — no solutions copied.';
            return;
        }
        const kept = Object.keys(
            this.persistence.carrySolutions(this.request.sourceGridData, this.request.isVortex, this.setPicker.getSelection()).gameStates
        ) as GameType[];
        const keptSet = new Set(kept);
        const dropped = this.carriable().filter(g => !keptSet.has(g));
        const keptText = kept.length
            ? `Carrying over ${kept.length}: ${kept.map(g => GAME_INITIALS[g]).join(' ')}`
            : 'No solved games are in this set';
        carry.textContent = dropped.length
            ? `${keptText} · dropping ${dropped.map(g => GAME_INITIALS[g]).join(' ')}`
            : keptText;
    }

    private confirm(): void {
        if (!this.setPicker) return;
        const result: DuplicateResult = {
            gameSet: this.setPicker.getScope(),
            copyScores: this.copyScores,
        };
        this.hide();
        this.callbacks.onConfirm(result);
    }
}
